import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { PaletteConfig } from '../config/palette-presets';

interface PaletteDemoProps {
  config: PaletteConfig;
}

const hexToRgba = (hex: string, alpha: number = 0.4): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const DashboardCard: React.FC<{ config: PaletteConfig; title: string; value: string; change: string; trend: 'up' | 'down' }> = ({ config, title, value, change, trend }) => (
  <div
    className="glass-effect rounded-xl p-6 border transition-all duration-200 cursor-pointer hover:scale-[1.02]"
    style={{
      backgroundColor: hexToRgba(config.background.card, 0.4),
      borderColor: config.border.default,
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.borderColor = config.border.hover;
      e.currentTarget.style.boxShadow = `0 0 20px ${config.glow}`;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.borderColor = config.border.default;
      e.currentTarget.style.boxShadow = 'none';
    }}
  >
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-sm font-medium" style={{ color: config.text.secondary }}>
        {title}
      </h3>
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: `${config.accent}20` }}
      >
        <div className="w-4 h-4 rounded" style={{ backgroundColor: config.accent }} />
      </div>
    </div>
    <div className="mb-2">
      <span className="text-2xl font-bold" style={{ color: config.text.primary }}>
        {value}
      </span>
    </div>
    <div className="flex items-center gap-2">
      <span
        className={`text-sm ${trend === 'up' ? 'text-green-400' : 'text-red-400'}`}
      >
        {trend === 'up' ? '↑' : '↓'} {change}
      </span>
      <span className="text-xs" style={{ color: config.text.muted }}>
        较上月
      </span>
    </div>
  </div>
);

const StatBar: React.FC<{ config: PaletteConfig; label: string; value: number; max: number }> = ({ config, label, value, max }) => {
  const percentage = (value / max) * 100;
  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm" style={{ color: config.text.secondary }}>{label}</span>
        <span className="text-sm font-semibold" style={{ color: config.text.primary }}>{value}%</span>
      </div>
      <div
        className="h-2 rounded-full overflow-hidden"
        style={{ backgroundColor: `${config.accent}15` }}
      >
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${percentage}%`,
            backgroundColor: config.accent,
            boxShadow: `0 0 10px ${config.glow}`,
          }}
        />
      </div>
    </div>
  );
};

const Button: React.FC<{ config: PaletteConfig; variant: 'primary' | 'secondary' | 'outline'; children: React.ReactNode; onClick?: () => void }> = ({ config, variant, children, onClick }) => {
  const baseStyle = 'px-6 py-3 rounded-lg font-medium transition-all duration-200 cursor-pointer';
  
  let style: React.CSSProperties = {};
  if (variant === 'primary') {
    style = {
      backgroundColor: `${config.accent}20`,
      color: config.accent,
      border: `1px solid ${config.border.default}`,
    };
  } else if (variant === 'secondary') {
    style = {
      backgroundColor: config.background.hover,
      color: config.text.primary,
      border: `1px solid ${config.border.default}`,
    };
  } else {
    style = {
      backgroundColor: 'transparent',
      color: config.accent,
      border: `1px solid ${config.border.default}`,
    };
  }

  return (
    <button
      className={baseStyle}
      style={style}
      onClick={onClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = config.border.hover;
        e.currentTarget.style.boxShadow = `0 0 15px ${config.glow}`;
        if (variant === 'primary') {
          e.currentTarget.style.backgroundColor = `${config.accent}30`;
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = style.border as string;
        e.currentTarget.style.boxShadow = 'none';
        if (variant === 'primary') {
          e.currentTarget.style.backgroundColor = style.backgroundColor as string;
        }
      }}
    >
      {children}
    </button>
  );
};

const Input: React.FC<{ config: PaletteConfig; label: string; placeholder: string }> = ({ config, label, placeholder }) => (
  <div className="mb-4">
    <label className="block text-sm mb-2" style={{ color: config.text.secondary }}>
      {label}
    </label>
    <input
      type="text"
      placeholder={placeholder}
      className="w-full px-4 py-3 rounded-lg transition-all duration-200 focus:outline-none"
      style={{
        backgroundColor: hexToRgba(config.background.card, 0.4),
        border: `1px solid ${config.border.default}`,
        color: config.text.primary,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = config.border.hover;
        e.currentTarget.style.boxShadow = `0 0 10px ${config.glow}`;
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = config.border.default;
        e.currentTarget.style.boxShadow = 'none';
      }}
    />
  </div>
);

const Table: React.FC<{ config: PaletteConfig }> = ({ config }) => {
  const data = [
    { id: 1, name: '项目A', status: '进行中', progress: 75, date: '2024-01-15' },
    { id: 2, name: '项目B', status: '已完成', progress: 100, date: '2024-01-10' },
    { id: 3, name: '项目C', status: '待开始', progress: 0, date: '2024-01-20' },
    { id: 4, name: '项目D', status: '进行中', progress: 45, date: '2024-01-12' },
  ];

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr style={{ borderBottom: `1px solid ${config.border.default}` }}>
            <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: config.text.secondary }}>项目名称</th>
            <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: config.text.secondary }}>状态</th>
            <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: config.text.secondary }}>进度</th>
            <th className="text-left py-3 px-4 text-sm font-semibold" style={{ color: config.text.secondary }}>日期</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr
              key={row.id}
              className="transition-all duration-200 cursor-pointer hover:bg-opacity-50"
              style={{
                borderBottom: `1px solid ${config.border.default}`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = config.background.hover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <td className="py-3 px-4" style={{ color: config.text.primary }}>{row.name}</td>
              <td className="py-3 px-4">
                <span
                  className="px-2 py-1 rounded text-xs font-medium"
                  style={{
                    backgroundColor: `${config.accent}20`,
                    color: config.accent,
                  }}
                >
                  {row.status}
                </span>
              </td>
              <td className="py-3 px-4">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ backgroundColor: `${config.accent}15` }}>
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${row.progress}%`,
                        backgroundColor: config.accent,
                        boxShadow: `0 0 8px ${config.glow}`,
                      }}
                    />
                  </div>
                  <span className="text-xs" style={{ color: config.text.muted }}>{row.progress}%</span>
                </div>
              </td>
              <td className="py-3 px-4 text-sm" style={{ color: config.text.secondary }}>{row.date}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Chart: React.FC<{ config: PaletteConfig }> = ({ config }) => {
  const chartData = [65, 78, 52, 89, 67, 94, 72];
  const maxValue = Math.max(...chartData);

  return (
    <div className="flex items-end justify-between h-48 gap-2">
      {chartData.map((value, index) => {
        const height = (value / maxValue) * 100;
        return (
          <div key={index} className="flex-1 flex flex-col items-center gap-2">
            <div
              className="w-full rounded-t transition-all duration-300 cursor-pointer hover:opacity-80"
              style={{
                height: `${height}%`,
                backgroundColor: config.accent,
                boxShadow: `0 0 15px ${config.glow}`,
                minHeight: '8px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = `0 0 25px ${config.glow}`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = `0 0 15px ${config.glow}`;
              }}
            />
            <span className="text-xs" style={{ color: config.text.muted }}>{value}</span>
          </div>
        );
      })}
    </div>
  );
};

const Modal: React.FC<{ config: PaletteConfig; isOpen: boolean; onClose: () => void; children: React.ReactNode }> = ({ config, isOpen, onClose, children }) => {
  if (!isOpen) return null;

  const modalContent = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: `blur(${config.glass.blur.small})`,
        WebkitBackdropFilter: `blur(${config.glass.blur.small})`,
        zIndex: 10000,
      }}
      onClick={onClose}
    >
      <div
        className="glass-effect rounded-xl p-6 border max-w-md w-full"
        style={{
          backgroundColor: config.glass.backgroundStrong,
          borderColor: config.glass.border,
          boxShadow: `0 0 40px ${config.glow}`,
          position: 'relative',
          backdropFilter: `blur(${config.glass.blur.large})`,
          WebkitBackdropFilter: `blur(${config.glass.blur.large})`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : null;
};

const Dropdown: React.FC<{ config: PaletteConfig; options: string[] }> = ({ config, options }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(options[0]);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef} style={{ zIndex: isOpen ? 10000 : 'auto' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 rounded-lg text-left flex items-center justify-between transition-all duration-200 cursor-pointer"
      style={{
        backgroundColor: hexToRgba(config.background.card, 0.4),
        border: `1px solid ${config.border.default}`,
        color: config.text.primary,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = config.border.hover;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = config.border.default;
        }}
      >
        <span>{selected}</span>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div
          className="absolute top-full left-0 right-0 mt-2 rounded-lg border overflow-hidden"
          style={{
            backgroundColor: hexToRgba(config.background.card, 0.85),
            borderColor: config.border.default,
            boxShadow: `0 0 20px ${config.glow}`,
            zIndex: 10000,
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
          }}
        >
          {options.map((option) => (
            <button
              key={option}
              onClick={() => {
                setSelected(option);
                setIsOpen(false);
              }}
              className="w-full px-4 py-2 text-left transition-all duration-200 cursor-pointer"
              style={{
                color: config.text.primary,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = config.background.hover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Notification: React.FC<{ config: PaletteConfig; type: 'success' | 'warning' | 'error'; message: string }> = ({ config, type, message }) => {
  const colors = {
    success: config.accent,
    warning: '#ffbf00',
    error: '#ff1744',
  };

  return (
    <div
      className="flex items-center gap-3 p-4 rounded-lg border"
      style={{
        backgroundColor: `${colors[type]}15`,
        borderColor: `${colors[type]}40`,
        boxShadow: `0 0 15px ${colors[type]}30`,
      }}
    >
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${colors[type]}30` }}
      >
        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: colors[type] }} />
      </div>
      <span style={{ color: config.text.primary }}>{message}</span>
    </div>
  );
};

const SearchBox: React.FC<{ config: PaletteConfig }> = ({ config }) => {
  const [value, setValue] = useState('');

  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="搜索..."
        className="w-full px-4 py-3 pl-10 rounded-lg transition-all duration-200 focus:outline-none"
      style={{
        backgroundColor: hexToRgba(config.background.card, 0.4),
        border: `1px solid ${config.border.default}`,
        color: config.text.primary,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = config.border.hover;
          e.currentTarget.style.boxShadow = `0 0 10px ${config.glow}`;
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = config.border.default;
          e.currentTarget.style.boxShadow = 'none';
        }}
      />
      <svg
        className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        style={{ color: config.text.muted }}
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    </div>
  );
};

const CardList: React.FC<{ config: PaletteConfig }> = ({ config }) => {
  const cards = [
    { title: '功能模块A', description: '这是一个功能描述', icon: '⚡' },
    { title: '功能模块B', description: '这是另一个功能描述', icon: '🎨' },
    { title: '功能模块C', description: '第三个功能描述', icon: '🚀' },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {cards.map((card, index) => (
        <div
          key={index}
          className="glass-effect rounded-xl p-6 border transition-all duration-200 cursor-pointer hover:scale-[1.02]"
          style={{
            backgroundColor: hexToRgba(config.background.card, 0.4),
            borderColor: config.border.default,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = config.border.hover;
            e.currentTarget.style.boxShadow = `0 0 20px ${config.glow}`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = config.border.default;
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div className="text-3xl mb-3">{card.icon}</div>
          <h4 className="text-lg font-semibold mb-2" style={{ color: config.text.primary }}>
            {card.title}
          </h4>
          <p className="text-sm" style={{ color: config.text.muted }}>
            {card.description}
          </p>
        </div>
      ))}
    </div>
  );
};

const Pagination: React.FC<{ config: PaletteConfig }> = ({ config }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = 5;

  return (
    <div className="flex items-center gap-2 justify-center">
      <button
        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
        className="px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer disabled:opacity-50"
      style={{
        backgroundColor: hexToRgba(config.background.card, 0.4),
        border: `1px solid ${config.border.default}`,
        color: config.text.primary,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
      >
        上一页
      </button>
      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
        <button
          key={page}
          onClick={() => setCurrentPage(page)}
          className="px-4 py-2 rounded-lg transition-all duration-200 cursor-pointer"
        style={{
          backgroundColor: currentPage === page ? `${config.accent}20` : hexToRgba(config.background.card, 0.4),
          border: `1px solid ${currentPage === page ? config.border.hover : config.border.default}`,
          color: currentPage === page ? config.accent : config.text.primary,
          boxShadow: currentPage === page ? `0 0 10px ${config.glow}` : 'none',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}
        >
          {page}
        </button>
      ))}
      <button
        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
        className="px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer disabled:opacity-50"
      style={{
        backgroundColor: hexToRgba(config.background.card, 0.4),
        border: `1px solid ${config.border.default}`,
        color: config.text.primary,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
      >
        下一页
      </button>
    </div>
  );
};

const Toggle: React.FC<{ config: PaletteConfig; checked: boolean; onChange: (checked: boolean) => void }> = ({ config, checked, onChange }) => {
  return (
    <div
      className="w-12 h-6 rounded-full relative cursor-pointer transition-all duration-200"
      style={{
        backgroundColor: checked ? `${config.accent}60` : `${config.accent}40`,
      }}
      onClick={() => onChange(!checked)}
    >
      <div
        className="w-5 h-5 rounded-full absolute top-0.5 transition-all duration-200"
        style={{
          left: checked ? 'calc(100% - 1.25rem)' : '0.125rem',
          backgroundColor: config.accent,
          boxShadow: `0 0 8px ${config.glow}`,
        }}
      />
    </div>
  );
};

const PaletteDemo: React.FC<PaletteDemoProps> = ({ config }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'analytics' | 'settings' | 'components'>('overview');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [notificationEnabled, setNotificationEnabled] = useState(true);
  const [autoUpdateEnabled, setAutoUpdateEnabled] = useState(false);

  return (
    <div className="space-y-6">
      <div
        className="glass-effect rounded-xl p-8 border"
        style={{
          backgroundColor: hexToRgba(config.background.card, 0.4),
          borderColor: config.border.default,
        }}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold mb-2" style={{ color: config.text.primary }}>
              {config.name}
            </h2>
            <p className="text-sm" style={{ color: config.text.muted }}>
              {config.description}
            </p>
          </div>
          <div className="flex gap-2">
            {(['overview', 'analytics', 'components', 'settings'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer"
                style={{
                  backgroundColor: activeTab === tab ? `${config.accent}20` : 'transparent',
                  color: activeTab === tab ? config.accent : config.text.secondary,
                  border: `1px solid ${activeTab === tab ? config.border.hover : config.border.default}`,
                }}
              >
                {tab === 'overview' && '概览'}
                {tab === 'analytics' && '分析'}
                {tab === 'components' && '组件'}
                {tab === 'settings' && '设置'}
              </button>
            ))}
          </div>
        </div>

        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <DashboardCard config={config} title="总用户数" value="12,345" change="+12.5%" trend="up" />
              <DashboardCard config={config} title="活跃用户" value="8,901" change="+8.2%" trend="up" />
              <DashboardCard config={config} title="收入" value="¥89.2K" change="-3.1%" trend="down" />
              <DashboardCard config={config} title="转化率" value="24.8%" change="+5.4%" trend="up" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div
                className="glass-effect rounded-xl p-6 border"
                style={{
                  backgroundColor: config.background.card,
                  borderColor: config.border.default,
                }}
              >
                <h3 className="text-lg font-semibold mb-4" style={{ color: config.text.primary }}>
                  性能指标
                </h3>
                <StatBar config={config} label="CPU 使用率" value={65} max={100} />
                <StatBar config={config} label="内存使用率" value={78} max={100} />
                <StatBar config={config} label="磁盘使用率" value={45} max={100} />
                <StatBar config={config} label="网络带宽" value={82} max={100} />
              </div>

              <div
                className="glass-effect rounded-xl p-6 border"
                style={{
                  backgroundColor: config.background.card,
                  borderColor: config.border.default,
                }}
              >
                <h3 className="text-lg font-semibold mb-4" style={{ color: config.text.primary }}>
                  操作面板
                </h3>
                <div className="space-y-3 mb-4">
                  <Button config={config} variant="primary">主要操作</Button>
                  <Button config={config} variant="secondary">次要操作</Button>
                  <Button config={config} variant="outline">轮廓按钮</Button>
                </div>
                <Input config={config} label="用户名" placeholder="请输入用户名" />
                <Input config={config} label="邮箱" placeholder="请输入邮箱地址" />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="glass-effect rounded-xl p-6 border text-center"
                  style={{
                    backgroundColor: config.background.card,
                    borderColor: config.border.default,
                  }}
                >
                  <div
                    className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                    style={{
                      backgroundColor: `${config.accent}20`,
                      boxShadow: `0 0 20px ${config.glow}`,
                    }}
                  >
                    <div className="w-8 h-8 rounded-full" style={{ backgroundColor: config.accent }} />
                  </div>
                  <h4 className="text-lg font-semibold mb-2" style={{ color: config.text.primary }}>
                    数据指标 {i}
                  </h4>
                  <p className="text-sm" style={{ color: config.text.muted }}>
                    详细的数据分析内容
                  </p>
                </div>
              ))}
            </div>
            <div
              className="glass-effect rounded-xl p-6 border"
              style={{
                backgroundColor: config.background.card,
                borderColor: config.border.default,
              }}
            >
              <h3 className="text-lg font-semibold mb-4" style={{ color: config.text.primary }}>
                数据图表
              </h3>
              <Chart config={config} />
            </div>
            <div
              className="glass-effect rounded-xl p-6 border"
              style={{
                backgroundColor: config.background.card,
                borderColor: config.border.default,
              }}
            >
              <h3 className="text-lg font-semibold mb-4" style={{ color: config.text.primary }}>
                数据表格
              </h3>
              <Table config={config} />
            </div>
          </div>
        )}

        {activeTab === 'components' && (
          <div className="space-y-6">
            <div
              className="glass-effect rounded-xl p-6 border"
              style={{
                backgroundColor: config.background.card,
                borderColor: config.border.default,
              }}
            >
              <h3 className="text-lg font-semibold mb-4" style={{ color: config.text.primary }}>
                搜索框
              </h3>
              <SearchBox config={config} />
            </div>

            <div
              className="glass-effect rounded-xl p-6 border"
              style={{
                backgroundColor: config.background.card,
                borderColor: config.border.default,
                position: 'relative',
                zIndex: 1000,
              }}
            >
              <h3 className="text-lg font-semibold mb-4" style={{ color: config.text.primary }}>
                下拉选择
              </h3>
              <Dropdown config={config} options={['选项一', '选项二', '选项三', '选项四']} />
            </div>

            <div
              className="glass-effect rounded-xl p-6 border"
              style={{
                backgroundColor: config.background.card,
                borderColor: config.border.default,
              }}
            >
              <h3 className="text-lg font-semibold mb-4" style={{ color: config.text.primary }}>
                通知提示
              </h3>
              <div className="space-y-3">
                <Notification config={config} type="success" message="操作成功完成" />
                <Notification config={config} type="warning" message="请注意相关事项" />
                <Notification config={config} type="error" message="操作失败，请重试" />
              </div>
            </div>

            <div
              className="glass-effect rounded-xl p-6 border"
              style={{
                backgroundColor: config.background.card,
                borderColor: config.border.default,
              }}
            >
              <h3 className="text-lg font-semibold mb-4" style={{ color: config.text.primary }}>
                卡片列表
              </h3>
              <CardList config={config} />
            </div>

            <div
              className="glass-effect rounded-xl p-6 border"
              style={{
                backgroundColor: config.background.card,
                borderColor: config.border.default,
              }}
            >
              <h3 className="text-lg font-semibold mb-4" style={{ color: config.text.primary }}>
                分页组件
              </h3>
              <Pagination config={config} />
            </div>

            <div
              className="glass-effect rounded-xl p-6 border"
              style={{
                backgroundColor: config.background.card,
                borderColor: config.border.default,
              }}
            >
              <h3 className="text-lg font-semibold mb-4" style={{ color: config.text.primary }}>
                模态框
              </h3>
              <Button config={config} variant="primary" onClick={() => setIsModalOpen(true)}>
                打开模态框
              </Button>
              <Modal config={config} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold" style={{ color: config.text.primary }}>
                    模态框标题
                  </h3>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 cursor-pointer"
                    style={{
                      backgroundColor: config.background.hover,
                      color: config.text.primary,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = config.background.darker;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = config.background.hover;
                    }}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <p className="mb-4" style={{ color: config.text.secondary }}>
                  这是一个模态框示例，展示了如何使用配色方案创建对话框组件。
                </p>
                <div className="flex gap-3 justify-end">
                  <Button config={config} variant="outline" onClick={() => setIsModalOpen(false)}>
                    取消
                  </Button>
                  <Button config={config} variant="primary" onClick={() => setIsModalOpen(false)}>
                    确认
                  </Button>
                </div>
              </Modal>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6">
            <div
              className="glass-effect rounded-xl p-6 border"
              style={{
                backgroundColor: config.background.card,
                borderColor: config.border.default,
              }}
            >
              <h3 className="text-lg font-semibold mb-4" style={{ color: config.text.primary }}>
                系统设置
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span style={{ color: config.text.secondary }}>通知推送</span>
                  <Toggle config={config} checked={notificationEnabled} onChange={setNotificationEnabled} />
                </div>
                <div className="flex items-center justify-between">
                  <span style={{ color: config.text.secondary }}>自动更新</span>
                  <Toggle config={config} checked={autoUpdateEnabled} onChange={setAutoUpdateEnabled} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaletteDemo;
